// controllers/scan_controller.js
import { Controller } from "@hotwired/stimulus";
import toast from "../utils/toast";

export default class extends Controller {
  static targets = ["reader", "result", "overlay"];
  static values = {
    startDelay: { type: Number, default: 100 },
    autoStart: { type: Boolean, default: true }, // false → start on demand (e.g. a "Scan QR" tap)
    qrbox: { type: Number, default: 250 },       // 0 → no scan-box overlay (frame provides its own)
  };

  connect() {
    this._starting = false;
    this._stopping = false;
    this._beforeRender = () => this.gracefulStop();

    document.addEventListener("turbo:before-render", this._beforeRender);
    if (this.autoStartValue) {
      this._timer = setTimeout(() => this.start(), this.startDelayValue);
    }
  }

  disconnect() {
    clearTimeout(this._timer);
    document.removeEventListener("turbo:before-render", this._beforeRender);
    // fire-and-forget; don't await inside disconnect
    this.gracefulStop();
  }

  async start() {
    if (!this.hasReaderTarget) return;

    // Hide the "Scan QR" overlay (if any) once the camera is being brought up.
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("hidden");

    if (this._stopping) await this._waitUntil(() => !this._stopping);

    if (this.html5) {
      await this.gracefulStop();
    }

    this._starting = true;

    try {
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");

      const devices = await Html5Qrcode.getCameras();
      if (!devices?.length) {
        toast("No cameras found", "error");
        return;
      }

      const backCameras = devices.filter(d => /back|rear|environment/i.test(d.label || ""));
      const preferredCamera =
        backCameras.find(d => !/wide/i.test(d.label || "")) ||
        backCameras[0] ||
        devices[0];
      const cameraId = preferredCamera.id;

      this.html5 = new Html5Qrcode(this.readerTarget.id, { verbose: false });

      await this.html5.start(
        cameraId,
        {
          fps: 10,
          ...(this.qrboxValue > 0 ? { qrbox: this.qrboxValue } : {}),
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        },
        (decodedText) => this.onDecode(decodedText),
      );

      const video = document.querySelector(`#${this.readerTarget.id} video`);
      if (video && video.srcObject) {
        const track = video.srcObject.getVideoTracks()[0];
        const capabilities = track.getCapabilities?.();
        if (capabilities?.focusMode?.includes("continuous")) {
          await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
        } 
      }

    } catch (err) {
      console.error("QR start error:", err);
      toast("Camera access failed", "error");
      this.html5 = null;
    } finally {
      this._starting = false;
    }
  }

  async gracefulStop() {
    if (!this.html5 || this._stopping) return;
    if (this._starting) await this._waitUntil(() => !this._starting);

    this._stopping = true;
    try {
      await this.html5.stop();
      this.html5.clear();
    } catch (err) {
      const msg = String(err || "");
      if (!msg.includes("under transition")) {
        console.warn("QR stop error:", err);
      }
    } finally {
      this.html5 = null;
      this._stopping = false;
    }
  }


  onDecode(text) {
    this.gracefulStop();

    try {
      const marker = "/send/to/";
      const index = text.indexOf(marker);
  
      if (index !== -1) {
        const lastPart = text.substring(index + marker.length);
        if (lastPart) {
          Turbo.visit(`/send/to/${encodeURIComponent(lastPart)}`, { action: "advance" });
          return;
        }
      }
      
      toast("Invalid QR code. Please try again.", "error");
      this.setContent(text);
      setTimeout(() => {  
        this.start();
      }, 800);
    } catch (err) {
      console.error("QR decode error:", err);
      toast("Error. Please try again.", "error");
      setTimeout(() => {
        this.start();
      }, 800);
    }
  }

  setContent(text) {
    this.resultTarget.classList.remove("hidden");
    if (this.hasResultTarget) this.resultTarget.textContent = `Scanned: ${text}`;
  }
  
  _waitUntil(pred, timeout = 1000) {
    return new Promise((resolve) => {
      const start = performance.now();
      const tick = () => (pred() || performance.now() - start > timeout) ? resolve() : requestAnimationFrame(tick);
      tick();
    });
  }
}
